import React, { type ReactElement } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as DeleteIcon } from '@assets/svg/manage-equipment/ic-delete.svg';
import { ReactComponent as EditIcon } from '@assets/svg/manage-equipment/ic-edit.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IAssetTracking } from '@model/assetTracking';

import {
  selectActiveAssetInfo,
  useAssetTrackerActions,
} from '@pages/GPSAssetTracking/store/useAssetTrackerStore';

import { useModalActions } from '@store/useModalStore';

import { useGPSAssetDeleteMutation } from '../../services/useGPSAssetDeleteMutation';
import style from './style.module.scss';

interface IProps {
  id: string;
}

const AssetContextMenu: React.FC<IProps> = ({ id }) => {
  const { t } = useTranslation();

  const { setActiveModal, setModalClose } = useModalActions();
  const { setClearActiveAsset } = useAssetTrackerActions();
  const activeAssetInfo = selectActiveAssetInfo();

  const { mutate: gpsAssetDeleteMutation } = useGPSAssetDeleteMutation();

  const handleDeleteAsset = (asset: IAssetTracking) => {
    let bodyTitle = t('Delete Asset Tracking');
    let bodyContent: ReactElement | string = t(
      'Are you sure you want to delete the selected asset tracking?',
    );

    if (asset.tracker.id) {
      bodyTitle += ` ${t('Serial')} ${asset.tracker.serialNumber}`;
      bodyContent = (
        <>
          {t('Are you sure you want to delete the selected asset tracking?')}
          <div className={style.extraText}>{t('Asset is associated with the GPS tracker')}</div>
          <div className={style.bodyContentField}>{asset.tracker.fullName}</div>
        </>
      );
    }

    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle,
        bodyContent,
        onConfirm: () => {
          gpsAssetDeleteMutation(asset?.id, {
            onSuccess: () => {
              showToast({ variant: 'success', message: t('Your changes have been saved.') });

              if (activeAssetInfo?.id === asset?.id) {
                setClearActiveAsset();
              }
              setModalClose();
            },
          });
        },
        confirmText: t('Delete'),
      },
    });
  };

  return (
    <Tooltip
      id={id}
      place="left"
      openOnClick
      className={style.contextMenuPopover}
      clickable
      disableStyleInjection
      noArrow
      render={({ content }) => {
        if (!content) {
          return null;
        }
        const row: IAssetTracking = JSON.parse(content);
        return (
          <>
            <div
              className={style.contextMenuPopoverItem}
              onClick={() =>
                setActiveModal({
                  name: MODAL_NAMES.createEditAssetTrackingModal,
                  options: { asset: row, gpsTracker: row.tracker },
                })
              }
            >
              <EditIcon className={style.menuIcon} />
              {t('Edit')}
            </div>

            <div className={style.contextMenuPopoverItem} onClick={() => handleDeleteAsset(row)}>
              <DeleteIcon className={style.menuIcon} />
              {t('Delete')}
            </div>
          </>
        );
      }}
    />
  );
};

export default AssetContextMenu;
